import { useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Quietly polls /activities/upcoming-reminders every 30 seconds.
 * When the backend reports an activity that should fire a reminder right now,
 * we display a sonner toast. The backend marks each reminder as "fired" for
 * the current user, so we never get the same toast twice.
 *
 * Mounted once at the app root (inside Layout) so it runs on every page.
 */
export default function ActivityReminderPoller() {
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get('/activities/upcoming-reminders');
        for (const r of data.reminders || []) {
          const when = r.scheduled_time ? `at ${r.scheduled_time}` : 'today';
          toast(`⏰ ${r.title}`, {
            description: `${r.activity_type.toUpperCase()} ${when}`,
            duration: 12000,
            action: {
              label: 'Open',
              onClick: () => { window.location.href = `/contact?id=${r.contact_id}`; },
            },
          });
        }
      } catch (err) {
        // 401 etc. — the poller silently retries on next tick.
      }
      // Schedule next tick AFTER the request completes so slow networks don't pile up requests
      if (!cancelled) timer = setTimeout(tick, 30000);
    };

    // First poll after 5 seconds (give the app time to settle)
    timer = setTimeout(tick, 5000);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return null;
}
